import { useState, useEffect } from 'react';
import { useQueryParams } from '../../hooks/useQueryParams';
import { useMonerisToken } from '../../hooks/useMonerisToken';
import { submitPayment, type PaymentResponse } from '../../services/paymentService';
import Layout from '../../components/Layout';
import OrderSummary from '../../components/OrderSummary';
import MonerisIframe from '../../components/MonerisIframe';
import StatusMessage from '../../components/StatusMessage';
import PayButton from '../../components/PayButton';
import './CheckoutPage.css';

function CheckoutPage() {
    const { params, errors, isValid } = useQueryParams();
    const { tokenState, requestToken, resetToken, setIframeRef } = useMonerisToken();

    const [paymentResult, setPaymentResult] = useState<PaymentResponse | null>(null);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    console.log('[Checkout] Render — params valid:', isValid, '| tokenState:', tokenState.dataKey ? 'HAS TOKEN' : 'no token');

    // When Moneris returns a token, submit payment to backend
    useEffect(() => {
        if (!tokenState.dataKey || !params) return;

        let cancelled = false;

        async function processPayment() {
            setIsSubmitting(true);
            setPaymentError(null);
            setPaymentResult(null);

            try {
                const response = await submitPayment({
                    amount: params!.amount,
                    currency: params!.currency,
                    customerName: params!.customerName,
                    customerEmail: params!.customerEmail,
                    orderId: params!.orderId,
                    tempToken: tokenState.dataKey!,
                });

                if (!cancelled) {
                    setPaymentResult(response);
                    console.log('[Checkout] Payment submitted successfully:', response);
                }
            } catch (err) {
                if (!cancelled) {
                    const message = err instanceof Error ? err.message : 'Payment submission failed.';
                    setPaymentError(message);
                    console.error('[Checkout] Payment submission error:', message);
                }
            } finally {
                if (!cancelled) {
                    setIsSubmitting(false);
                }
            }
        }

        processPayment();

        return () => {
            cancelled = true;
        };
    }, [tokenState.dataKey, params]);

    // Validation failed — show errors, do not render checkout UI
    if (!isValid) {
        return (
            <Layout>
                <StatusMessage
                    type="error"
                    title="Invalid Checkout Parameters"
                    messages={errors}
                />
            </Layout>
        );
    }

    // Iframe returned success — submit to backend and show result
    if (tokenState.dataKey) {
        if (isSubmitting) {
            return (
                <Layout>
                    <StatusMessage type="info" title="Processing Payment">
                        <p>Submitting payment to server, please wait...</p>
                    </StatusMessage>
                    <OrderSummary params={params} />
                </Layout>
            );
        }

        if (paymentError) {
            return (
                <Layout>
                    <StatusMessage type="error" title="Payment Failed">
                        <p>{paymentError}</p>
                    </StatusMessage>
                    <OrderSummary params={params} />
                </Layout>
            );
        }

        if (paymentResult) {
            return (
                <Layout>
                    <StatusMessage
                        type={paymentResult.id ? 'success' : 'error'}
                        title={paymentResult.id ? 'Payment Successful' : 'Payment Failed'}
                    >
                        <p>{paymentResult.message}</p>
                        {paymentResult.id && (
                            <p><strong>Transaction ID:</strong> {paymentResult.id}</p>
                        )}
                    </StatusMessage>
                    <OrderSummary params={params} />
                </Layout>
            );
        }

        return (
            <Layout>
                <StatusMessage type="info" title="Processing...">
                    <p>Initializing payment...</p>
                </StatusMessage>
            </Layout>
        );
    }

    const handlePayClick = () => {
        console.log('[Checkout] ---- PAY NOW CLICKED ----');
        resetToken();
        requestToken();
    };

    const isProcessing = tokenState.isTokenizing;

    return (
        <Layout>
            <div className="checkout-page">
                <OrderSummary params={params} />

                <MonerisIframe
                    profileId={params!.profileId}
                    setIframeRef={setIframeRef}
                    fieldErrors={tokenState.fieldErrors}
                />

                <PayButton
                    onClick={handlePayClick}
                    loading={isProcessing}
                    disabled={isProcessing}
                    label={isProcessing ? 'Processing...' : 'Pay Now'}
                />
            </div>
        </Layout>
    );
}

export default CheckoutPage;
